/**
 * Decision logging system for AI debugging
 * Helps understand why AI made certain decisions
 */

import { UTILITY_CONSTANTS } from "@/core/battle/ai/utils/AIConfig";

export interface DecisionContext {
    unitId: string;
    unitName: string;
    stance: string;
    position: string;
    currentHp: string;
    currentStamina: number;
}

export interface UtilityComponents {
    // Attack components
    baseDamage?: number;
    hitChance?: number;
    expectedDamage?: number;
    woundedBonus?: number;

    proximity?: number;
    safety?: number;
    accessibility?: number;


    // immediateBonus?: number;
    
    // // Move components
    // progress?: number;
    // positioning?: number;
    // repositioning?: number;
    // distanceBonus?: number;
    // threatGradient?: number;
    // adjacency?: number;
    // oppRisk?: number;
    // moveCost?: number;
    // trappedPenalty?: number;
    // oscillation?: number;
    // enteringRange?: number;
    // unnecessaryMove?: number;
    // retreatBonus?: number;
    // enteringRangePenalty?: number;
    // threat?: number;
    // threatZone?: number;
    // waitPenalty?: number;
    // pathAlignment?: number;
    // threatDelta?: number;
    // cantAttackAfter?: number;
    // kiting?: number;
    waiting?: number;
    
    // Total
    total: number;
}

export interface EvaluatedActionLog {
    type: 'attack' | 'move' | 'wait';
    description: string;
    position?: string;
    targetId?: string;
    skillId?: string;
    staminaCost: number;
    staminaAfter: number;
    utility: number;
    components: UtilityComponents;
}

export interface DecisionLog {
    context: DecisionContext;
    candidatesCount: number;
    topCandidates: EvaluatedActionLog[];  // Top 5
    finalDecision: EvaluatedActionLog | null;
    planDepth: number;  // How many actions planned ahead
    timestamp: number;
}

export class AIDecisionLogger {
    private logs: DecisionLog[] = [];
    private enabled: boolean;
    private maxLogs: number = 50;  // Keep last 50 decisions

    constructor(enabled: boolean = false) {
        this.enabled = enabled;
    }

    enable(): void {
        this.enabled = true;
    }

    disable(): void {
        this.enabled = false;
    }

    logDecision(log: DecisionLog): void {
        if (!this.enabled) return;
        
        this.logs.push(log);
        
        // Keep only last N logs
        if (this.logs.length > this.maxLogs) {
            this.logs.shift();
        }

        // Pretty print to console
        this.prettyPrint(log);
    }

    private prettyPrint(log: DecisionLog): void {
        console.group(`AI Decision: ${log.context.unitName} (${log.context.stance})`);
        console.log(`Position: ${log.context.position}, Stamina: ${log.context.currentStamina}, HP: ${log.context.currentHp}`);
        console.log(`Evaluated ${log.candidatesCount} candidates, showing top ${log.topCandidates.length}:`);
        
        console.table(log.topCandidates.map(c => ({
            Action: c.description,
            Utility: c.utility.toFixed(2),
            'Stamina After': c.staminaAfter,
            'Key Component': this.getKeyComponent(c.components)
        })));

        if (log.finalDecision) {
            console.log(`Final Decision: ${log.finalDecision.description} (utility: ${log.finalDecision.utility.toFixed(1)})`);
            if (log.planDepth > 1) {
                console.log(`   Part of ${log.planDepth}-action sequence`);
            }
            
            // Show detailed breakdown for final decision
            if (log.finalDecision.type === 'move') {
                console.log('   Components:', {
                    pos: log.finalDecision.components.proximity?.toFixed(1),
                    path: log.finalDecision.components.safety?.toFixed(1),
                    threat: log.finalDecision.components.accessibility?.toFixed(1),
                    // opp: log.finalDecision.components.oppRisk?.toFixed(1),
                    // cost: log.finalDecision.components.moveCost?.toFixed(1),
                });
            } else if (log.finalDecision.type === 'attack') {
                console.log('   Components:', {
                    base: log.finalDecision.components.baseDamage?.toFixed(1),
                    weight: log.finalDecision.components.hitChance?.toFixed(1),
                    target: log.finalDecision.components.expectedDamage?.toFixed(1),
                    wounded: log.finalDecision.components.woundedBonus?.toFixed(1)
                });
            }
        } else {
            console.log('No valid decision');
        }
        
        console.groupEnd();
    }

    private getKeyComponent(components: UtilityComponents): string {
        const entries = Object.entries(components)
            .filter(([key, val]) => key !== 'total' && val !== undefined && val !== 0)
            .map(([key, val]) => ({ key, val: Math.abs(val as number) }))
            .sort((a, b) => b.val - a.val);
        
        return entries.length > 0 ? entries[0]!.key : '-';
    }

    getLastLog(): DecisionLog | null {
        return this.logs[this.logs.length - 1] || null;
    }

    getAllLogs(): DecisionLog[] {
        return [...this.logs];
    }

    getLogsForUnit(unitId: string): DecisionLog[] {
        return this.logs.filter(log => log.context.unitId === unitId);
    }

    clearLogs(): void {
        this.logs = [];
    }

    /**
     * Export logs as JSON for analysis
     */
    exportLogs(): string {
        return JSON.stringify(this.logs, null, 2);
    }
}

// Global logger instance
export const aiDecisionLogger = new AIDecisionLogger(false); // Set to true for debugging